import authFetch from './httpClient';
import { IResult } from '../models/HttpResult';
import { IPagedResult } from '../models/HttpPagedResult';
import { ContractConceptCategory, NewContractConceptCategory } from '../models/Entities/Contracts/ContractConceptCategory';
import * as Functions from '../functions/common';
import * as Sentry from '@sentry/react';

const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'contracts/concepts/categories';

/** Tope del servidor (GetAllContractConceptCategoriesQueryValidator.MaxPageSize); más que eso es 400. */
const PAGE_SIZE = 100;
/** Corte de seguridad: un tenant tiene decenas de categorías, no miles. */
const MAX_PAGES = 20;

const requestRaw = async <T>(url: string, options: RequestInit, context: string): Promise<T> => {
    try {
        const response = await authFetch(url, {
            ...options,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                ...(options.headers as Record<string, string> | undefined)
            }
        });

        if (!response.ok) {
            Sentry.captureException(response);
            const body = await response.text();
            throw Functions.HttpErrorHandler(body, response);
        }

        return await response.json() as T;
    } catch (error) {
        Sentry.captureException(error, { tags: { context } });
        throw error;
    }
};

const request = async <T>(url: string, options: RequestInit, context: string): Promise<T> =>
    (await requestRaw<IResult<T>>(url, options, context)).data;

/** Todas las categorías del tenant, recorriendo las páginas que haga falta. */
export const GetAllContractConceptCategories = async (): Promise<ContractConceptCategory[]> => {
    const all: ContractConceptCategory[] = [];
    for (let page = 1; page <= MAX_PAGES; page++) {
        const url = `${BaseURL}?PageNumber=${page}&PageSize=${PAGE_SIZE}`;
        const result = await requestRaw<IPagedResult<ContractConceptCategory[]>>(url, { method: 'GET' }, 'GetAllContractConceptCategories');
        const items = Array.isArray(result?.data) ? result.data : [];
        all.push(...items);
        const totalPages = result?.totalPages ?? 1;
        if (items.length < PAGE_SIZE || page >= totalPages) break;
    }
    return all;
};

export const GetContractConceptCategoryById = async (id: number): Promise<ContractConceptCategory> =>
    request<ContractConceptCategory>(`${BaseURL}/${id}`, { method: 'GET' }, 'GetContractConceptCategoryById');

export const CreateContractConceptCategory = async (category: NewContractConceptCategory): Promise<ContractConceptCategory> =>
    request<ContractConceptCategory>(BaseURL, { method: 'POST', body: JSON.stringify(category) }, 'CreateContractConceptCategory');

export const UpdateContractConceptCategory = async (category: ContractConceptCategory): Promise<ContractConceptCategory> =>
    request<ContractConceptCategory>(`${BaseURL}/${category.id}`, {
        method: 'PUT',
        body: JSON.stringify({ id: category.id, name: category.name, description: category.description ?? null })
    }, 'UpdateContractConceptCategory');

/** Rechazado por el servidor mientras algún concepto del contrato la use. */
export const DeleteContractConceptCategory = async (id: number): Promise<number> =>
    request<number>(`${BaseURL}/${id}`, { method: 'DELETE' }, 'DeleteContractConceptCategory');
